import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnChanges, OnDestroy, inject, input } from '@angular/core';
import { Observable, ReplaySubject, Subject, combineLatest, map } from 'rxjs';

import { ProductContextDirective } from 'ish-core/directives/product-context.directive';
import { AppFacade } from 'ish-core/facades/app.facade';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { DeviceType } from 'ish-core/models/viewtype/viewtype.types';
import { whenTruthy } from 'ish-core/utils/operators';

import { DotCmsUveFacade } from '../../facades/dot-cms-uve.facade';
import { ProductToolTipContentlet, TooltipDetails } from '../../models/dot-cms-uve-contentlet.model';
import { DotCmsUveProductItemComponent } from '../dot-cms-uve-product-carousel/dot-cms-uve-product-carousel-list/dot-cms-uve-product-item/dot-cms-uve-product-item.component';

@Component({
  selector: 'custom-dot-cms-uve-products-tooltip',
  templateUrl: './dot-cms-uve-products-tooltip.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./dot-cms-uve-products-tooltip.component.scss'],
  imports: [NgClass, AsyncPipe, DotCmsUveProductItemComponent, ProductContextDirective],
})
export class DotCmsUveProductsTooltipComponent implements OnChanges, OnDestroy {
  contentlet = input.required<ProductToolTipContentlet>();
  private destroy$ = new Subject<void>();
  deviceType$: Observable<DeviceType>;
  tooltipDetails: TooltipDetails[];
  dotcmsHost$: Observable<string>;
  triggerCount = 1;
  productFailed: boolean;

  contentlet$ = new ReplaySubject<ProductToolTipContentlet>(1);

  constructor(
    private appFacade: AppFacade,
    private elementRef: ElementRef,
    private gtmFacade: GoogleTagManagerFacade
  ) {}
  private dotCmsUveFacade = inject(DotCmsUveFacade);
  ngOnChanges() {
    this.tooltipDetails = JSON.parse(this.contentlet().search);
    this.contentlet$.next(this.contentlet());
    this.deviceType$ = this.appFacade.deviceType$;
    this.dotcmsHost$ = this.dotCmsUveFacade.getDotCmsHost$;
    const gtmProduct: ProductView[] = this.contentlet()
      .productSkus?.split(',')
      .map(item => {
        const skus = item.trim();
        return { sku: skus };
      });
    this.gtmFacade.setupIntersectionObserverForGTMEvent(
      this.elementRef.nativeElement,
      gtmProduct,
      this.triggerCount,
      'view_item_list'
    );
  }

  showPopup(deviceType: string, productSku: string) {
    if (deviceType !== 'desktop') {
      this.appFacade.openQuickView(productSku);
    }
  }

  // eslint-disable-next-line complexity
  getDisplayStyle(deviceType: string, product: TooltipDetails): boolean {
    switch (deviceType) {
      case 'desktop':
        return product.show ? true : false;
      case 'tablet':
        return product.showmd ? true : false;
      case 'mobile':
        return product.showsm ? true : false;
      default:
        return false;
    }
  }

  getTopPosition(deviceType: string, product: TooltipDetails): string {
    if (deviceType === 'desktop') {
      return product.top;
    } else if (deviceType === 'tablet') {
      return product.topmd ?? product.top;
    } else {
      return product.topsm ?? product.topmd ?? product.top;
    }
  }

  getLeftPosition(deviceType: string, product: TooltipDetails): string {
    if (deviceType === 'desktop') {
      return product.left;
    } else if (deviceType === 'tablet') {
      return product.leftmd ?? product.left;
    } else {
      return product.leftsm ?? product.leftmd ?? product.left;
    }
  }
  getImageSrc(deviceType: string): Observable<string> {
    if (deviceType === 'desktop') {
      return combineLatest([this.dotcmsHost$.pipe(whenTruthy())]).pipe(
        whenTruthy(),
        map(([host]) => `${host + this.contentlet().image.idPath}`)
      );
    } else if (deviceType === 'tablet') {
      return this.generateImageURIFromImageKey(this.contentlet().imagemd.idPath ? 'imagemd' : 'image');
    } else {
      // assuming deviceType is 'mobile'
      return this.generateImageURIFromImageKey(
        this.contentlet().imagesm.idPath ? 'imagesm' : this.contentlet().imagemd.idPath ? 'imagemd' : 'image'
      );
    }
  }

  onProductFailed(failed: boolean) {
    this.productFailed = failed;
  }

  private generateImageURIFromImageKey(imagekey: string): Observable<string> {
    return combineLatest([this.dotCmsUveFacade.getDotCmsHost$.pipe(whenTruthy())]).pipe(
      whenTruthy(),
      //@ts-ignore
      map(([host]) => `${host + this.contentlet()[`${imagekey}`].idPath}`)
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
